import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar as CalendarIcon, 
  CheckCircle, 
  XCircle, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  User,
  MoreVertical,
  ChevronDown,
  MessageCircle
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

// Mock data for mini charts
const miniData = [
  { value: 40 }, { value: 65 }, { value: 45 }, { value: 90 }, { value: 55 }, { value: 75 }, { value: 85 }
];

const attendanceData = [
  { date: '06/05', turnos: 28, presentes: 24 },
  { date: '07/05', turnos: 112, presentes: 95 },
  { date: '08/05', turnos: 0, presentes: 0 },
  { date: '09/05', turnos: 0, presentes: 0 },
  { date: '10/05', turnos: 0, presentes: 0 },
  { date: '11/05', turnos: 90, presentes: 62 },
  { date: '12/05', turnos: 78, presentes: 0 },
];

const MiniChart = ({ color, data = miniData }) => (
  <div className="mini-chart-container">
    <ResponsiveContainer width="100%" height={60}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`color-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="value" 
          stroke={color} 
          strokeWidth={2} 
          fillOpacity={1} 
          fill={`url(#color-${color})`} 
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const StatCardV2 = ({ title, value, subValue, color, data }) => (
  <div className="card stat-card-v2">
    <div className="stat-info">
      <p className="stat-label">{title}</p>
      <p className="stat-sublabel">{subValue}</p>
    </div>
    <MiniChart color={color} data={data} />
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAppointments: 0,
    totalPatients: 0,
    attendanceRate: 0,
    cancellations: 0,
    present: 0,
    pending: 0,
    balance: 0
  });
  const [nextAppointments, setNextAppointments] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotifications();

  // Load active staff from localStorage
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';
  
  const [selectedArea, setSelectedArea] = useState(isAdmin ? 'TODOS' : (activeStaff.full_name || ''));
  const [professionals, setProfessionals] = useState([]);

  const pieData = [
    { name: 'Pendientes', value: stats.pending, color: '#FFB300' },
    { name: 'Presentes/Completados', value: stats.present, color: '#00E676' },
    { name: 'Cancelados', value: stats.cancellations, color: '#FF5252' },
  ];

  const attendanceChartData = weeklyData.length > 0 ? weeklyData : [
    { date: 'Hoy', turnos: stats.totalAppointments, presentes: stats.present }
  ];

  useEffect(() => {
    if (isAdmin) {
      supabase.from('professionals').select('full_name').order('full_name').then(({ data }) => {
        if (data) setProfessionals(data.map(p => p.full_name));
      });
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStats();
    
    let debounceTimer;
    
    const debouncedFetchStats = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchStats();
      }, 1500);
    };
    
    const subscription = supabase
      .channel('public:dashboard_stats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, debouncedFetchStats)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions' }, debouncedFetchStats)
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(subscription);
    };
  }, [selectedArea]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      let appQuery = supabase.from('appointments').select('*', { count: 'exact', head: true });
      let patQuery = supabase.from('patients').select('*', { count: 'exact', head: true });
      let canQuery = supabase.from('appointments').select('*', { count: 'exact', head: true }).eq('status', 'cancelado');
      let presQuery = supabase.from('appointments').select('*', { count: 'exact', head: true }).eq('status', 'completado');
      let pendQuery = supabase.from('appointments').select('*', { count: 'exact', head: true }).eq('status', 'pendiente');
      let transQuery = supabase.from('transactions').select('amount, type');
      let nextAppsQuery = supabase.from('appointments').select('*').in('status', ['pendiente', 'presente', 'en-llamado']).order('appointment_time', { ascending: true }).limit(10);
      
      const today = new Date();
      const pastWeek = new Date();
      pastWeek.setDate(today.getDate() - 6);
      const isoPastWeek = pastWeek.toISOString().split('T')[0];
      
      let weeklyQuery = supabase.from('appointments').select('appointment_date, status').gte('appointment_date', isoPastWeek);

      if (selectedArea && selectedArea !== 'TODOS') {
        appQuery = appQuery.eq('professional_name', selectedArea);
        patQuery = patQuery.eq('professional_name', selectedArea); // Wait, patients might not have professional_name, but keeping as it was
        canQuery = canQuery.eq('professional_name', selectedArea);
        presQuery = presQuery.eq('professional_name', selectedArea);
        pendQuery = pendQuery.eq('professional_name', selectedArea);
        transQuery = transQuery.eq('professional_name', selectedArea);
        nextAppsQuery = nextAppsQuery.eq('professional_name', selectedArea);
        weeklyQuery = weeklyQuery.eq('professional_name', selectedArea);
      }
      
      const [
        { count: appCount },
        { count: patCount },
        { count: canCount },
        { count: presCount },
        { count: pendCount },
        { data: transData },
        { data: weeklyApps }
      ] = await Promise.all([appQuery, patQuery, canQuery, presQuery, pendQuery, transQuery, weeklyQuery]);
        
      const currentBalance = transData ? transData.reduce((acc, t) => t.type === 'ingreso' ? acc + parseFloat(t.amount) : acc - parseFloat(t.amount), 0) : 0;

      setStats({
        totalAppointments: appCount || 0,
        totalPatients: patCount || 0,
        attendanceRate: appCount ? Math.round((presCount / appCount) * 100) : 0,
        cancellations: canCount || 0,
        present: presCount || 0,
        pending: pendCount || 0,
        balance: currentBalance
      });

      // Compute weekly chart data
      if (weeklyApps) {
        const countsByDate = {};
        for (let i = 0; i < 7; i++) {
          const d = new Date(pastWeek);
          d.setDate(d.getDate() + i);
          const dateStr = d.toISOString().split('T')[0];
          countsByDate[dateStr] = { turnos: 0, presentes: 0, date: dateStr.substring(5).replace('-', '/') };
        }
        
        weeklyApps.forEach(app => {
          const d = app.appointment_date;
          if (countsByDate[d]) {
            countsByDate[d].turnos += 1;
            if (['completado', 'atendido', 'presente'].includes(app.status)) {
              countsByDate[d].presentes += 1;
            }
          }
        });
        
        setWeeklyData(Object.values(countsByDate));
      }

      // Fetch next appointments for the dashboard
      const todayIso = today.toISOString().split('T')[0];
      const { data: nextApps } = await nextAppsQuery.eq('appointment_date', todayIso);
      
      setNextAppointments(nextApps || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', id);
      if (error) throw error;
      addNotification(`Estado actualizado a ${newStatus.toUpperCase()}`, 'success');
      fetchStats();
    } catch (err) {
      addNotification('Error al actualizar estado', 'error');
    }
  };

  return (
    <div className="dashboard-v2">
      {isAdmin && (
        <div className="dashboard-header-controls" style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
          <span className="area-label" style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Vista de Área:</span>
          <select 
            value={selectedArea} 
            onChange={(e) => setSelectedArea(e.target.value)}
            style={{ padding: '8px 15px', borderRadius: '8px', minWidth: '240px' }}
          >
            <option value="TODOS">TODAS LAS ÁREAS (General)</option>
            {professionals.map((p, idx) => (
              <option key={idx} value={p}>{p}</option>
            ))}
          </select>
        </div>
      )}
      <div className="stats-header-grid">
        <StatCardV2 
          title="Total Turnos" 
          subValue={`Histórico: ${stats.totalAppointments}`} 
          color="#7C4DFF" 
          data={[{value: 20}, {value: 45}, {value: 30}, {value: 80}, {value: 20}, {value: 60}, {value: 50}]}
        />
        <StatCardV2 
          title="Turnos Presentes" 
          subValue={`Histórico: ${stats.present}`} 
          color="#00BFA5" 
          data={[{value: 10}, {value: 30}, {value: 20}, {value: 70}, {value: 15}, {value: 50}, {value: 40}]}
        />
        <StatCardV2 
          title="Recaudación" 
          subValue={`Total: ${stats.balance.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}`} 
          color="#0066ff" 
          data={[{value: 5}, {value: 15}, {value: 25}, {value: 10}, {value: 5}, {value: 40}, {value: 80}]}
        />
        <StatCardV2 
          title="Pacientes" 
          subValue={`Base de Datos: ${stats.totalPatients}`} 
          color="#FF5252" 
          data={[{value: 5}, {value: 40}, {value: 15}, {value: 5}, {value: 45}, {value: 60}, {value: 30}]}
        />
      </div>

      <div className="main-charts-grid">
        <div className="card donut-chart-card">
          <div className="flex-between">
            <h3>Distribución de Turnos</h3>
            <div className="date-filter">Real-time <ChevronDown size={14} /></div>
          </div>
          <div className="donut-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0b0b14', 
                    border: '1px solid rgba(0, 229, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: 'white'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="donut-center-text">
              <p>Total:</p>
              <h2>{stats.totalAppointments}</h2>
            </div>
          </div>
          <div className="donut-legend">
            {pieData.map(item => (
              <div key={item.name} className="legend-item">
                <span className="dot" style={{ backgroundColor: item.color }}></span>
                {item.name}
              </div>
            ))}
          </div>
        </div>

        <div className="card bar-chart-card">
          <div className="flex-between">
            <h3>Presentismo</h3>
            <div className="date-filter">Últimos 7 días <ChevronDown size={14} /></div>
          </div>
          <div className="bar-container">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={attendanceChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: 'var(--text-muted)', fontWeight: 600 }} 
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: 'var(--text-muted)', fontWeight: 600 }} 
                />
                <Tooltip 
                  cursor={{ fill: 'rgba(255,255,255,0.02)' }}
                  contentStyle={{ 
                    backgroundColor: '#0b0b14', 
                    border: '1px solid rgba(0, 229, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: 'white'
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  align="right" 
                  iconType="circle" 
                  wrapperStyle={{ paddingBottom: '20px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em' }}
                />
                <Bar dataKey="turnos" name="Total Turnos" fill="#0066ff" radius={[6, 6, 0, 0]} barSize={30} />
                <Bar dataKey="presentes" name="Total Turnos Presentes" fill="#00ff7f" radius={[6, 6, 0, 0]} barSize={30} />
              </BarChart>
            </ResponsiveContainer>
            <div className="y-axis-label">Cant. Turnos</div>
            <div className="x-axis-label">Fecha</div>
          </div>
        </div>
      </div>

      <div className="dashboard-operational-grid">
        <div className="card next-apps-card">
          <div className="flex-between">
            <h3>Sala de Espera y Próximos Turnos</h3>
            <button className="btn-link" onClick={() => navigate('/waiting-room')}>Ver Sala Completa</button>
          </div>
          <div className="operational-table-container">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>HORA</th>
                  <th>PACIENTE</th>
                  <th>PROFESIONAL</th>
                  <th>ESTADO</th>
                  <th className="text-right">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center-p">Sincronizando...</td></tr>
                ) : nextAppointments.length === 0 ? (
                  <tr><td colSpan="5" className="no-data-msg">No hay turnos próximos para hoy.</td></tr>
                ) : nextAppointments.map(app => (
                  <tr key={app.id} className={`dash-row status-${app.status}`}>
                    <td className="time-cell">{app.appointment_time?.substring(0, 5)}</td>
                    <td>
                      <div className="p-cell">
                        <span className="p-name">{app.patient_name}</span>
                        <span className="p-dni">{app.patient_dni}</span>
                      </div>
                    </td>
                    <td><span className="prof-tag">{app.professional_name}</span></td>
                    <td>
                      <div className={`status-pill ${app.status}`}>
                        {app.status === 'presente' ? 'EN ESPERA' : app.status === 'en-llamado' ? 'LLAMANDO' : 'PENDIENTE'}
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="dash-actions">
                        {app.status === 'pendiente' && (
                          <button className="btn-dash admit" onClick={() => handleUpdateStatus(app.id, 'presente')} title="Admitir">
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {app.status === 'presente' && (
                          <button className="btn-dash call" onClick={() => handleUpdateStatus(app.id, 'en-llamado')} title="Llamar">
                            <TrendingUp size={16} />
                          </button>
                        )}
                        <button className="btn-dash delete" onClick={() => handleUpdateStatus(app.id, 'cancelado')} title="Cancelar">
                          <XCircle size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
